import { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";

const GOLD         = "#C9A96E";
const GOLD_PALE    = "#F0DEB4";
const GOLD_BG      = "#FEF9EE";
const INK          = "#1C1915";
const INK_SOFT     = "#4A4540";
const INK_MUTED    = "#9A9390";
const BORDER       = "#E7E1D8";
const SOFT_BG      = "#F7F5F2";
const WHITE        = "#FFFFFF";
const RED          = "#C0392B";
const RED_BG       = "#FDEDEC";
const RED_BORDER   = "#F2C4C0";
const BLUE         = "#2E6DB4";
const BLUE_BG      = "#EEF4FD";
const BLUE_BORDER  = "#C5D9F2";
const GREEN        = "#1A7A44";
const GREEN_BG     = "#E8F6EF";
const GREEN_BORDER = "#B2DEC8";
const ORANGE       = "#E87A2A";
const ORANGE_BG    = "#FEF0E6";
const ORANGE_BORDER= "#F5C9A0";

const TASK_STATUTS = [
  { id: "a_faire",   label: "À faire",   color: INK_MUTED, bg: SOFT_BG,      border: BORDER },
  { id: "en_cours",  label: "En cours",  color: ORANGE,    bg: ORANGE_BG,    border: ORANGE_BORDER },
  { id: "termine",   label: "Terminé",   color: GREEN,    bg: GREEN_BG,     border: GREEN_BORDER },
];

function getTaskStatut(id) {
  return TASK_STATUTS.find((s) => s.id === id) || TASK_STATUTS[0];
}

function getPriorityStyle(priority) {
  if (priority === "haute") return { color: RED, bg: RED_BG, border: RED_BORDER };
  if (priority === "basse") return { color: INK_MUTED, bg: SOFT_BG, border: BORDER };
  return { color: BLUE, bg: BLUE_BG, border: BLUE_BORDER };
}

const STATUTS = [
  { id: "diagnostic",  label: "Diagnostic",  color: BLUE,   bg: BLUE_BG,   border: BLUE_BORDER },
  { id: "conception",  label: "Conception",  color: GOLD,   bg: GOLD_BG,   border: GOLD_PALE },
  { id: "chiffrage",   label: "Chiffrage",   color: ORANGE, bg: ORANGE_BG, border: ORANGE_BORDER },
  { id: "chantier",    label: "Chantier",    color: GREEN,  bg: GREEN_BG,  border: GREEN_BORDER },
  { id: "livraison",   label: "Livraison",   color: INK,    bg: SOFT_BG,   border: BORDER },
  { id: "termine",     label: "Terminé",     color: INK_MUTED, bg: SOFT_BG, border: BORDER },
];

function getStatut(id) {
  return STATUTS.find(s => s.id === id) || { label: id || "—", color: INK_MUTED, bg: SOFT_BG, border: BORDER };
}

/** Extrait des infos brief depuis la description / projet pour le moodboard */
function extraireBriefProjet(project, isOPCMandate = false) {
  const desc = (project?.description || "").trim();
  const styleMatch = desc.match(/(?:style|esthétique)\s*[:\-]?\s*([^.]+)/i) || desc.match(/(contemporain|moderne|traditionnel|minimaliste|industriel|scandinave|bois|marbre|luxe)/i);
  const couleursMatch = desc.match(/(?:couleur|palette)\s*[:\-]?\s*([^.]+)/i) || desc.match(/(blanc|noir|gris|beige|bleu|vert|terracotta|doré|cuivré)/gi);
  const materiauxMatch = desc.match(/(?:matériau|matériaux)\s*[:\-]?\s*([^.]+)/i) || desc.match(/(bois|marbre|pierre|béton|métal|cuir|lin|velours)/gi);
  const piecesMatch = desc.match(/(?:pièce|espace|salle)\s*[:\-]?\s*([^.]+)/i) || desc.match(/(salon|cuisine|chambre|sdb|salle de bain|dressing|bureau|entrée|terrasse)/gi);
  const styleVal = styleMatch ? String(styleMatch[1] != null ? styleMatch[1] : styleMatch[0]).trim().slice(0, 80) : "";
  return {
    typeProjet: project?.status ? getStatut(project.status).label : (project?.name || "").slice(0, 30) || "—",
    budget: (() => {
      if (isOPCMandate) return "—";
      if (project?.budget != null && project?.budget !== "") {
        const n = Number(project.budget);
        const valeur = Number.isFinite(n) ? n.toLocaleString("fr-FR") : String(project.budget);
        return `${valeur} MAD`;
      }
      return "Non renseigné";
    })(),
    style: styleVal || (desc ? "À affiner à partir du brief" : "Non renseigné"),
    couleurs: couleursMatch ? [...new Set(couleursMatch)].join(", ") : (desc ? "À affiner à partir du brief" : "Non renseigné"),
    materiaux: materiauxMatch ? [...new Set(materiauxMatch)].join(", ") : (desc ? "À affiner à partir du brief" : "Non renseigné"),
    pieces: piecesMatch ? [...new Set(piecesMatch)].join(", ") : (desc ? "À définir par pièce" : "Non renseigné"),
    ambiance: desc ? desc.slice(0, 400) : "À renseigner dans la description du projet.",
  };
}

/** Génère la structure texte du moodboard à partir du projet */
function genererStructureMoodboard(project, brief) {
  const nom = project?.name || "Projet";
  const client = project?.client_name || "—";
  const styleDominant = brief.style || "À définir avec le client";
  const paletteCouleurs = brief.couleurs || "À définir";
  const materiauxRecommandes = brief.materiaux || "À définir";
  const ambiance = brief.ambiance || "À renseigner";
  const pieces = (brief.pieces || "").split(/\s*,\s*/).filter(Boolean);
  const suggestionsParPiece = pieces.length > 0
    ? pieces.map((p) => ({ piece: p.trim(), suggestion: `Ambiance cohérente avec le style ${styleDominant}, palette ${paletteCouleurs}, matériaux ${materiauxRecommandes}.` }))
    : [{ piece: "Général", suggestion: `Style ${styleDominant}. Palette : ${paletteCouleurs}. Matériaux : ${materiauxRecommandes}. Ambiance : ${ambiance.slice(0, 120)}…` }];
  return {
    titre: `Moodboard — ${nom}`,
    client,
    typeProjet: brief.typeProjet,
    budget: brief.budget,
    styleDominant,
    paletteCouleurs,
    materiauxRecommandes,
    ambiance,
    suggestionsParPiece,
    dateGeneration: new Date().toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }),
  };
}

/** Exporte le brief moodboard en fichier texte */
function exporterBriefMoodboard(moodboard) {
  if (!moodboard) return;
  const lignes = [
    moodboard.titre,
    "=".repeat(50),
    `Client : ${moodboard.client}`,
    `Type de projet : ${moodboard.typeProjet}`,
    `Budget : ${moodboard.budget}`,
    "",
    "Style dominant",
    "-".repeat(30),
    moodboard.styleDominant,
    "",
    "Palette couleurs",
    "-".repeat(30),
    moodboard.paletteCouleurs,
    "",
    "Matériaux recommandés",
    "-".repeat(30),
    moodboard.materiauxRecommandes,
    "",
    "Ambiance",
    "-".repeat(30),
    moodboard.ambiance,
    "",
    "Suggestions visuelles par pièce",
    "-".repeat(30),
    ...moodboard.suggestionsParPiece.map((s) => `${s.piece} : ${s.suggestion}`),
    "",
    `Généré le ${moodboard.dateGeneration}`,
  ];
  const blob = new Blob([lignes.join("\n")], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `brief-moodboard-${(moodboard.titre || "projet").replace(/[^a-z0-9-]/gi, "-").toLowerCase()}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

const FORM_VIDE = {
  name: "", client_name: "", status: "conception",
  budget: "", progress: 0, description: ""
};

export default function ProjectsPage({
  openProjectId,
  setActiveTab: navigateTo,
  /** Si défini, n’affiche que les projets dont client_email correspond (insensible à la casse). */
  clientEmail = null,
  /** Si défini (rôle OPC), n’affiche que les projets liés dans opc_projets. */
  opcUserId = null,
  /** OPC avec mandat : pas de montants ni saisies budgétaires. */
  isOPCMandate = false,
  cabinetId = null,
}) {
  /** Montant MAD affiché : {isOPCMandate ? "—" : `${valeur} MAD`} */
  function madDisplay(raw) {
    if (!raw && raw !== 0) return "—";
    const n = Number(raw);
    const valeur = Number.isFinite(n) ? n.toLocaleString("fr-FR") : String(raw);
    return isOPCMandate ? "—" : `${valeur} MAD`;
  }

  const clientRestricted = Boolean(clientEmail);
  const opcRestricted = Boolean(opcUserId);
  const canCreateProject = !clientRestricted && !opcRestricted;
  const [projects,     setProjects]     = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [showForm,     setShowForm]     = useState(false);
  const [form,         setForm]         = useState(FORM_VIDE);
  const [saving,       setSaving]       = useState(false);
  const [filterStatut, setFilterStatut] = useState("tous");
  const [search,       setSearch]       = useState("");
  const [editId,       setEditId]       = useState(null);
  const [confirmDel,   setConfirmDel]   = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [tabFiche, setTabFiche] = useState("infos");
  const [savingDetail, setSavingDetail] = useState(false);
  const [projectMilestones, setProjectMilestones] = useState([]);
  const [loadingJalons, setLoadingJalons] = useState(false);
  const [projectTasks, setProjectTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: "", priority: "normale", due_date: "", assigned_to: "" });
  const [savingTask, setSavingTask] = useState(false);
  const [moodboardGenerated, setMoodboardGenerated] = useState(null);

  const [projectDashboard, setProjectDashboard] = useState(null);

  const projectFilterKey =
    clientEmail == null || String(clientEmail).trim() === ""
      ? "__ALL__"
      : `ce:${String(clientEmail).trim().toLowerCase()}`;
  const opcFilterKey = opcUserId || "__NO_OPC__";

  const [opcNoAssignments, setOpcNoAssignments] = useState(false);

  useEffect(() => {
    if (!openProjectId || !navigateTo || projects.length === 0) return;
    const projet = projects.find((p) => String(p.id) === String(openProjectId));
    if (projet) {
      setSelectedProject(projet);
      navigateTo("projets");
    }
  }, [openProjectId, projects, navigateTo]);

  useEffect(() => {
    setMoodboardGenerated(null);
    if (!selectedProject) {
      setProjectDashboard(null);
      return;
    }
    const statutLabel = getStatut(selectedProject.status).label;
    const budgetTxt = madDisplay(selectedProject.budget);
    const avancementPct = progressFromJalons();
    const nbTaches = projectTasks.length;
    const nbJalons = projectMilestones.length;
    const prochainJalon = projectMilestones
      .filter((m) => m.due_date && (m.status !== "termine" && m.status !== "done"))
      .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))[0] || null;
    const hasDevisAccepte = false; // sera alimenté lors du chargement des propositions si besoin
    const alerts = [];
    if (!hasDevisAccepte) alerts.push("Aucun devis accepté pour ce projet.");
    if (nbJalons === 0) alerts.push("Aucun jalon défini pour ce projet.");
    if (nbTaches === 0) alerts.push("Aucune tâche créée pour ce projet.");
    setProjectDashboard({
      statutLabel,
      budgetTxt,
      avancementPct,
      nbTaches,
      nbJalons,
      prochainJalon,
      alerts,
    });
  }, [selectedProject, projectTasks, projectMilestones, isOPCMandate]);

  useEffect(() => {
    if (!selectedProject?.id) {
      setProjectMilestones([]);
      return;
    }
    let cancelled = false;
    setLoadingJalons(true);
    supabase
      .from("milestones")
      .select("*")
      .eq("project_id", selectedProject.id)
      .eq("cabinet_id", cabinetId)
      .order("number", { ascending: true })
      .then(({ data }) => {
        if (!cancelled && data) setProjectMilestones(data);
        else if (!cancelled) setProjectMilestones([]);
      })
      .finally(() => { if (!cancelled) setLoadingJalons(false); });
    return () => { cancelled = true; };
  }, [selectedProject?.id, cabinetId]);

  useEffect(() => {
    if (!selectedProject?.id) {
      setProjectTasks([]);
      return;
    }
    let cancelled = false;
    setLoadingTasks(true);
    supabase
      .from("project_tasks")
      .select("*")
      .eq("project_id", selectedProject.id)
      .eq("cabinet_id", cabinetId)
      .order("due_date", { ascending: true, nullsFirst: false })
      .then(({ data }) => {
        if (!cancelled && data) setProjectTasks(data);
        else if (!cancelled) setProjectTasks([]);
      })
      .finally(() => { if (!cancelled) setLoadingTasks(false); });
    return () => { cancelled = true; };
  }, [selectedProject?.id, cabinetId]);

  async function charger() {
    if (cabinetId == null) {
      setProjects([]);
      setOpcNoAssignments(false);
      setLoading(true);
      return;
    }
    setLoading(true);
    setOpcNoAssignments(false);
    let list = [];
    if (opcUserId) {
      const { data: links } = await supabase
        .from("opc_projets")
        .select("project_id")
        .eq("opc_id", opcUserId)
          .eq("cabinet_id", cabinetId);
      const ids = (links || []).map((l) => l.project_id).filter(Boolean);
      if (ids.length === 0) {
        setOpcNoAssignments(true);
        setProjects([]);
        setLoading(false);
        return;
      }
      const { data } = await supabase
        .from("projects")
        .select("*")
        .in("id", ids)
        .order("created_at", { ascending: false });
      list = data || [];
    } else {
      const { data } = await supabase
        .from("projects")
        .select("*")
        .eq("cabinet_id", cabinetId)
        .order("created_at", { ascending: false });
      list = data || [];
      if (clientEmail != null && String(clientEmail).trim() !== "") {
        const em = String(clientEmail).trim().toLowerCase();
        list = list.filter((p) => (p.client_email || "").trim().toLowerCase() === em);
      }
    }
    setProjects(list);
    setLoading(false);
  }

  function field(k, v) {
    setForm(prev => ({ ...prev, [k]: v }));
  }

  async function sauvegarder() {
    if (!form.name || !form.client_name) return;
    setSaving(true);
    const budgetForSave = isOPCMandate && editId
      ? (projects.find((p) => String(p.id) === String(editId))?.budget ?? form.budget)
      : form.budget;
    if (editId) {
      await supabase.from("projects").update({
        name: form.name, client_name: form.client_name,
        status: form.status, budget: budgetForSave,
        progress: Number(form.progress), description: form.description
      }).eq("id", editId).eq("cabinet_id", cabinetId);
    } else {
      await supabase.from("projects").insert([{
        name: form.name, client_name: form.client_name,
        status: form.status, budget: isOPCMandate ? null : form.budget,
        progress: Number(form.progress), description: form.description,
        cabinet_id: cabinetId
      }]);
    }
    setSaving(false);
    setShowForm(false);
    setForm(FORM_VIDE);
    setEditId(null);
    charger();
  }

  function editer(p) {
    setForm({
      name: p.name || "", client_name: p.client_name || "",
      status: p.status || "conception", budget: p.budget || "",
      progress: p.progress || 0, description: p.description || ""
    });
    setEditId(p.id);
    setShowForm(true);
  }

  async function supprimer(id) {
    await supabase.from("projects").delete().eq("id", id).eq("cabinet_id", cabinetId);
    if (selectedProject?.id === id) {
      setSelectedProject(null);
    }
    setConfirmDel(null);
    charger();
  }
function ouvrirProjet(p) {
  setSelectedProject(p);
  setTabFiche("infos");
}

function majProjetLocal(updated) {
  setProjects(prev => prev.map(p => (p.id === updated.id ? updated : p)));
  setSelectedProject(updated);
}

async function changerStatutProjet(projectId, newStatus) {
  if (!selectedProject) return;
  setSavingDetail(true);

  console.log("CLICK statut:", { projectId, newStatus });

  const { data, error } = await supabase
    .from("projects")
    .update({ status: newStatus })
    .eq("id", projectId)
    .eq("cabinet_id", cabinetId)
    .select()
    .single();

  console.log("RESULT update statut:", { data, error });

  setSavingDetail(false);

  if (error) {
    alert("Erreur update statut: " + error.message);
    return;
  }

  if (data) {
    majProjetLocal(data);
  }
}

async function enregistrerNotesProjet() {
  if (!selectedProject) return;
  setSavingDetail(true);

  const { data, error } = await supabase
    .from("projects")
    .update({ description: selectedProject.description || "" })
    .eq("id", selectedProject.id)
    .eq("cabinet_id", cabinetId)
    .select()
    .single();

  setSavingDetail(false);

  if (!error && data) {
    majProjetLocal(data);
  }
}

  function progressFromJalons() {
    const total = projectMilestones.length;
    if (total === 0) return selectedProject?.progress ?? 0;
    const termines = projectMilestones.filter(
      (m) => m.status === "termine" || m.status === "done"
    ).length;
    return Math.round((termines / total) * 100);
  }

  async function updateJalonStatus(milestoneId, newStatus) {
    if (!selectedProject) return;
    setSavingDetail(true);
    const payload = { status: newStatus };
    if (newStatus === "termine") {
      payload.date_validation = new Date().toISOString();
    }
    const { error } = await supabase
      .from("milestones")
      .update(payload)
      .eq("id", milestoneId)
      .eq("cabinet_id", cabinetId);
    if (error) {
      setSavingDetail(false);
      alert("Erreur mise à jour jalon : " + error.message);
      return;
    }
    const updated = projectMilestones.map((m) =>
      m.id === milestoneId ? { ...m, status: newStatus, ...payload } : m
    );
    setProjectMilestones(updated);
    const total = updated.length;
    const newProgress = total
      ? Math.round(
          (updated.filter((m) => m.status === "termine" || m.status === "done").length / total) * 100
        )
      : selectedProject.progress ?? 0;
    const { data: projectData, error: projectError } = await supabase
      .from("projects")
      .update({ progress: newProgress })
      .eq("id", selectedProject.id)
      .eq("cabinet_id", cabinetId)
      .select()
      .single();
    setSavingDetail(false);
    if (!projectError && projectData) majProjetLocal(projectData);
  }

  async function sauvegarderTache() {
    if (!selectedProject?.id || !taskForm.title?.trim()) return;
    setSavingTask(true);
    const { data, error } = await supabase
      .from("project_tasks")
      .insert([{
        project_id: selectedProject.id,
        title: taskForm.title.trim(),
        priority: taskForm.priority || "normale",
        due_date: taskForm.due_date || null,
        status: "a_faire",
        assigned_to: taskForm.assigned_to?.trim() || null,
        cabinet_id: cabinetId,
      }])
      .select()
      .single();
    setSavingTask(false);
    if (error) {
      alert("Erreur création tâche : " + error.message);
      return;
    }
    setProjectTasks((prev) => [...prev, data].sort((a, b) => {
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      return new Date(a.due_date) - new Date(b.due_date);
    }));
    setTaskForm({ title: "", priority: "normale", due_date: "", assigned_to: "" });
    setShowTaskForm(false);
  }

  async function changerAssignedTache(taskId, value) {
    const assigned_to = value?.trim() || null;
    const { error } = await supabase
      .from("project_tasks")
      .update({ assigned_to })
      .eq("id", taskId)
      .eq("cabinet_id", cabinetId);
    if (error) {
      alert("Erreur mise à jour assignation : " + error.message);
      return;
    }
    setProjectTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, assigned_to } : t))
    );
  }

  async function changerStatutTache(taskId, newStatus) {
    const { error } = await supabase
      .from("project_tasks")
      .update({ status: newStatus })
      .eq("id", taskId)
      .eq("cabinet_id", cabinetId);
    if (error) {
      alert("Erreur mise à jour statut : " + error.message);
      return;
    }
    setProjectTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, status: newStatus } : t))
    );
  }

  const nbTotal = projects.length;
  const nbActifs = projects.filter((p) => ["chantier", "livraison"].includes(p.status)).length;
  const nbEtude = projects.filter((p) => ["diagnostic", "conception", "chiffrage"].includes(p.status)).length;
  const nbTermines = projects.filter((p) => p.status === "termine").length;

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif", color: INK, maxWidth: 1300, margin: "0 auto", background: "#F8F4EE", padding: "12px 0 20px" }}><style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;700&family=DM+Sans:wght@400;500;700&display=swap');
      `}</style>

      {/* EN-TÊTE */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}><div><div style={{ fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", color: "#C09A5B", fontWeight: 700, marginBottom: 4 }}>
            YOMA DESIGN — Suivi
          </div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 700, color: "#3D2E20" }}>Projets</div><div style={{ fontSize: 12, color: INK_MUTED, marginTop: 4 }}>
            {projects.length} projet{projects.length !== 1 ? "s" : ""} au total
          </div></div>{canCreateProject && (
        <button onClick={() => { setForm(FORM_VIDE); setEditId(null); setShowForm(true); }}
          style={{ display: "flex", alignItems: "center", gap: 7, padding: "10px 20px",
            background: "#C09A5B", border: "none", borderRadius: 9,
            fontSize: 12, fontWeight: 700, color: WHITE, cursor: "pointer" }}>
          ＋ Nouveau projet
        </button>
        )}</div>

      {opcNoAssignments && (
        <div
          style={{
            marginBottom: 18,
            padding: "18px 20px",
            borderRadius: 12,
            background: ORANGE_BG,
            border: `1px solid ${ORANGE_BORDER}`,
            color: INK_SOFT,
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1.5,
          }}
        >
          Aucun projet assigné. Contactez votre administrateur.
        </div>
      )}

      {/* KPI */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 12, marginBottom: 18 }}><div style={{ background: "#FFFFFF", border: `1px solid ${BORDER}`, borderRadius: 12, padding: "14px 16px" }}><div style={{ fontSize: 11, color: INK_MUTED, marginBottom: 8 }}>Total projets</div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, color: "#3D2E20", fontWeight: 700 }}>{nbTotal}</div></div><div style={{ background: "#FFFFFF", border: "1px solid rgba(92,138,92,0.35)", borderRadius: 12, padding: "14px 16px" }}><div style={{ fontSize: 11, color: INK_MUTED, marginBottom: 8 }}>Projets actifs</div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, color: "#5C8A5C", fontWeight: 700 }}>{nbActifs}</div></div><div style={{ background: "#FFFFFF", border: "1px solid rgba(192,154,91,0.35)", borderRadius: 12, padding: "14px 16px" }}><div style={{ fontSize: 11, color: INK_MUTED, marginBottom: 8 }}>Projets en étude</div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, color: "#C09A5B", fontWeight: 700 }}>{nbEtude}</div></div><div style={{ background: "#FFFFFF", border: "1px solid rgba(139,115,97,0.35)", borderRadius: 12, padding: "14px 16px" }}><div style={{ fontSize: 11, color: INK_MUTED, marginBottom: 8 }}>Projets terminés</div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, color: "#8B7361", fontWeight: 700 }}>{nbTermines}</div></div></div>

      {/* COMPTEURS PAR STATUT */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}><div onClick={() => setFilterStatut("tous")}
          style={{ padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600,
            background: filterStatut === "tous" ? INK : WHITE,
            color: filterStatut === "tous" ? WHITE : INK_SOFT,
            border: `1px solid ${filterStatut === "tous" ? INK : BORDER}` }}>
          Tous ({projects.length})
        </div>
        {STATUTS.map(s => {
          const n = projects.filter(p => p.status === s.id).length;
          if (n === 0) return null;
          const active = filterStatut === s.id;
          return (
            <div key={s.id} onClick={() => setFilterStatut(active ? "tous" : s.id)}
              style={{ padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600,
                background: active ? s.bg : WHITE,
                color: active ? s.color : INK_SOFT,
                border: `1px solid ${active ? s.color : BORDER}` }}>
              {s.label} ({n})
            </div>
          );
        })}
      </div>

      {/* RECHERCHE */}
      <div style={{ marginBottom: 16 }}><input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher un projet ou un client…"
          style={{ width: "100%", maxWidth: 400, padding: "10px 14px", borderRadius: 8,
            border: `1px solid ${BORDER}`, fontSize: 13, color: INK, background: WHITE,
            outline: "none", boxSizing: "border-box" }} /></div>

      {/* FORMULAIRE */}
      {showForm && (
        <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 14,
          padding: "24px 28px", marginBottom: 20, boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 700, color: "#3D2E20" }}>
              {editId ? "Modifier le projet" : "Nouveau projet"}
            </div><button onClick={() => { setShowForm(false); setForm(FORM_VIDE); setEditId(null); }}
              style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: INK_MUTED }}>×</button></div><div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 14 }}>
            {[
              { label: "Nom du projet *", key: "name", placeholder: "ex: Villa Jacaranda" },
              { label: "Nom du client *", key: "client_name", placeholder: "ex: M. Bennani" },
              { label: "Budget (MAD)", key: "budget", placeholder: "ex: 850000" },
            ].map((f) => {
              if (f.key === "budget" && isOPCMandate) return null;
              return (
                <div key={f.key}><label style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase",
                    color: INK_SOFT, fontWeight: 700, display: "block", marginBottom: 6 }}>
                    {f.label}
                  </label><input value={form[f.key]} onChange={e => field(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 8,
                      border: `1px solid ${BORDER}`, fontSize: 13, color: INK,
                      outline: "none", boxSizing: "border-box" }} /></div>
              );
            })}

            <div><label style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase",
                color: INK_SOFT, fontWeight: 700, display: "block", marginBottom: 6 }}>
                Statut
              </label><select value={form.status} onChange={e => field("status", e.target.value)}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8,
                  border: `1px solid ${BORDER}`, fontSize: 13, color: INK,
                  background: WHITE, outline: "none", boxSizing: "border-box" }}>
                {STATUTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select></div><div style={{ gridColumn: "2 / -1" }}><label style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase",
                color: INK_SOFT, fontWeight: 700, display: "block", marginBottom: 6 }}>
                Description / notes
              </label><textarea value={form.description} onChange={e => field("description", e.target.value)}
                placeholder="Notes internes, adresse, type de mission…"
                rows={2}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8,
                  border: `1px solid ${BORDER}`, fontSize: 13, color: INK,
                  resize: "vertical", outline: "none", boxSizing: "border-box" }} /></div><div style={{ gridColumn: "1 / -1" }}><label style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase",
                color: INK_SOFT, fontWeight: 700, display: "block", marginBottom: 6 }}>
                Avancement — {form.progress}%
              </label><input type="range" min="0" max="100" value={form.progress}
                onChange={e => field("progress", e.target.value)}
                style={{ width: "100%", accentColor: GOLD }} /></div></div><div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}><button onClick={() => { setShowForm(false); setForm(FORM_VIDE); setEditId(null); }}
              style={{ padding: "10px 20px", background: WHITE, border: `1px solid ${BORDER}`,
                borderRadius: 8, fontSize: 12, fontWeight: 600, color: INK_SOFT, cursor: "pointer" }}>
              Annuler
            </button><button onClick={sauvegarder} disabled={saving || !form.name || !form.client_name}
              style={{ padding: "10px 24px", background: form.name && form.client_name ? "#C09A5B" : "#DDD",
                border: "none", borderRadius: 8, fontSize: 12, fontWeight: 700,
                color: WHITE, cursor: form.name && form.client_name ? "pointer" : "not-allowed" }}>
              {saving ? "Enregistrement…" : editId ? "Mettre à jour" : "Ajouter"}
            </button></div></div>
      )}

{selectedProject && (
  <div
    style={{
      background: WHITE,
      border: `1px solid ${BORDER}`,
      borderRadius: 14,
      padding: "24px 28px",
      marginBottom: 20,
      boxShadow: "0 4px 24px rgba(0,0,0,0.06)"
    }}
  ><div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}><div><div style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: GOLD, fontWeight: 700, marginBottom: 6 }}>
          Fiche projet
        </div><div style={{ fontSize: 20, fontWeight: 800, color: INK }}>
          {selectedProject.name}
        </div><div style={{ fontSize: 12, color: INK_MUTED, marginTop: 4 }}>
          Client : {selectedProject.client_name || "—"}
        </div></div><div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {navigateTo && (
          <button
            onClick={() => navigateTo("proposition-contractuelle:" + selectedProject.id)}
            style={{
              padding: "8px 16px",
              background: GOLD_BG,
              border: `1px solid ${GOLD_PALE}`,
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 700,
              color: GOLD,
              cursor: "pointer"
            }}
          >
            Lancer la proposition contractuelle
          </button>
        )}
        <button
          onClick={() => setSelectedProject(null)}
          style={{
            background: "none",
            border: "none",
            fontSize: 18,
            cursor: "pointer",
            color: INK_MUTED
          }}
        >
          ×
        </button></div></div><div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
      {[
        { id: "infos", label: "Infos" },
        { id: "etapes", label: "Étapes" },
        { id: "jalons", label: "Jalons" },
        { id: "taches", label: "Tâches" },
        { id: "documents", label: "Documents" },
        { id: "generation", label: "Génération projet" },
        { id: "notes", label: "Notes" },
      ].map(tab => (
        <button
          key={tab.id}
          onClick={() => setTabFiche(tab.id)}
          style={{
            padding: "8px 14px",
            borderRadius: 8,
            border: `1px solid ${tabFiche === tab.id ? GOLD : BORDER}`,
            background: tabFiche === tab.id ? GOLD_BG : WHITE,
            color: tabFiche === tab.id ? GOLD : INK_SOFT,
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer"
          }}
        >
          {tab.label}
        </button>
      ))}
    </div>

    {tabFiche === "infos" && (
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        {projectDashboard && (
          <div
            style={{
              padding: "18px 20px",
              borderRadius: 12,
              border: `1px solid ${BORDER}`,
              background: SOFT_BG,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: 14,
            }}
          ><div><div style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: INK_MUTED, marginBottom: 4 }}>
                Statut
              </div><div style={{ fontSize: 14, fontWeight: 700, color: INK }}>
                {projectDashboard.statutLabel}
              </div></div>
              {!isOPCMandate && (
                <div>
                  <div>
                    <div style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: INK_MUTED, marginBottom: 4 }}>
                      Budget
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: INK }}>
                      {projectDashboard.budgetTxt}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: INK_MUTED, marginBottom: 4 }}>
                      Devis accepté
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: INK }}>
                      {/* à alimenter avec le montant accepté lorsqu'il sera disponible */}
                      —
                    </div>
                  </div>
                </div>
              )}<div><div style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: INK_MUTED, marginBottom: 4 }}>
                Avancement
              </div><div style={{ display: "flex", alignItems: "center", gap: 8 }}><div style={{ flex: 1, height: 6, background: WHITE, borderRadius: 999, overflow: "hidden" }}><div
                    style={{
                      height: "100%",
                      width: `${projectDashboard.avancementPct || 0}%`,
                      background: GOLD,
                      borderRadius: 999,
                      transition: "width 0.3s ease",
                    }}
                  /></div><span style={{ fontSize: 13, fontWeight: 700, color: GOLD, minWidth: 32, textAlign: "right" }}>
                  {projectDashboard.avancementPct || 0}%
                </span></div></div><div><div style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: INK_MUTED, marginBottom: 4 }}>
                Tâches
              </div><div style={{ fontSize: 14, fontWeight: 700, color: INK }}>
                {projectDashboard.nbTaches}
              </div></div><div><div style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: INK_MUTED, marginBottom: 4 }}>
                Jalons
              </div><div style={{ fontSize: 14, fontWeight: 700, color: INK }}>
                {projectDashboard.nbJalons}
              </div></div><div style={{ gridColumn: "1 / -1" }}><div style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: INK_MUTED, marginBottom: 4 }}>
                Prochain jalon
              </div><div style={{ fontSize: 13, color: INK_SOFT }}>
                {projectDashboard.prochainJalon
                  ? `${projectDashboard.prochainJalon.title || "Jalon"} — ${fmtDate(projectDashboard.prochainJalon.due_date)}`
                  : "Aucun jalon à venir"}
              </div></div>
            {projectDashboard.alerts && projectDashboard.alerts.length > 0 && (
              <div style={{ gridColumn: "1 / -1" }}><div
                  style={{
                    marginTop: 4,
                    padding: "10px 12px",
                    borderRadius: 10,
                    background: RED_BG,
                    border: `1px solid ${RED_BORDER}`,
                    color: RED,
                    fontSize: 12,
                  }}
                ><strong style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                    Alertes
                  </strong><ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                    {projectDashboard.alerts.map((a, idx) => (
                      <li key={idx}>{a}</li>
                    ))}
                  </ul></div></div>
            )}
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}><div><div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", color: INK_MUTED, marginBottom: 4 }}>Nom du projet</div><div style={{ fontSize: 14, color: INK, fontWeight: 600 }}>{selectedProject.name || "—"}</div></div><div><div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", color: INK_MUTED, marginBottom: 4 }}>Client</div><div style={{ fontSize: 14, color: INK, fontWeight: 600 }}>{selectedProject.client_name || "—"}</div></div><div><div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", color: INK_MUTED, marginBottom: 4 }}>Statut</div><div style={{ fontSize: 14, color: INK, fontWeight: 600 }}>{getStatut(selectedProject.status).label}</div></div>{!isOPCMandate && (<div><div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", color: INK_MUTED, marginBottom: 4 }}>Budget</div><div style={{ fontSize: 14, color: INK, fontWeight: 600 }}>{madDisplay(selectedProject.budget)}</div></div>)}<div><div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", color: INK_MUTED, marginBottom: 4 }}>Avancement</div><div style={{ fontSize: 14, color: INK, fontWeight: 600 }}>{selectedProject.progress || 0}%</div></div><div><div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", color: INK_MUTED, marginBottom: 4 }}>Créé le</div><div style={{ fontSize: 14, color: INK, fontWeight: 600 }}>{fmtDate(selectedProject.created_at)}</div></div><div style={{ gridColumn: "1 / -1" }}><div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", color: INK_MUTED, marginBottom: 4 }}>Description</div><div style={{ fontSize: 13, color: INK_SOFT, lineHeight: 1.7 }}>
              {selectedProject.description || "Aucune description"}
            </div></div></div></div>
    )}

    {tabFiche === "etapes" && (
      <div><div style={{ fontSize: 12, color: INK_MUTED, marginBottom: 14 }}>
          Clique sur un statut pour faire avancer le projet.
        </div><div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {STATUTS.map(s => {
            const active = selectedProject.status === s.id;
            return (
              <button
                key={s.id}
                onClick={() => changerStatutProjet(selectedProject.id, s.id)}
                disabled={savingDetail}
                style={{
                  padding: "10px 14px",
                  borderRadius: 8,
                  border: `1px solid ${active ? s.color : s.border}`,
                  background: active ? s.bg : WHITE,
                  color: active ? s.color : INK_SOFT,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: savingDetail ? "wait" : "pointer",
                  opacity: savingDetail ? 0.7 : 1
                }}
              >
                {s.label}
              </button>
            );
          })}
        </div></div>
    )}

    {tabFiche === "jalons" && (
      <div><div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}><div style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: INK_MUTED, fontWeight: 700 }}>
            Avancement (calculé)
          </div><div style={{ flex: 1, maxWidth: 280, height: 8, background: SOFT_BG, borderRadius: 999, overflow: "hidden" }}><div
              style={{
                height: "100%",
                width: `${progressFromJalons()}%`,
                background: GOLD,
                borderRadius: 999,
                transition: "width 0.3s ease"
              }}
            /></div><span style={{ fontSize: 13, fontWeight: 800, color: GOLD, minWidth: 36 }}>
            {progressFromJalons()}%
          </span></div>
        {loadingJalons ? (
          <div style={{ padding: 24, textAlign: "center", color: INK_MUTED, fontSize: 13 }}>
            Chargement des jalons…
          </div>
        ) : projectMilestones.length === 0 ? (
          <div
            style={{
              padding: "20px",
              borderRadius: 10,
              background: SOFT_BG,
              border: `1px dashed ${BORDER}`,
              color: INK_MUTED,
              fontSize: 13
            }}
          >
            Aucun jalon pour ce projet. Créez-en depuis la page Jalons (menu).
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {projectMilestones.map((j) => {
              const isTermine = j.status === "termine" || j.status === "done";
              return (
                <div
                  key={j.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "12px 14px",
                    background: isTermine ? GREEN_BG : SOFT_BG,
                    border: `1px solid ${isTermine ? GREEN_BORDER : BORDER}`,
                    borderRadius: 10
                  }}
                ><span style={{ fontSize: 12, fontWeight: 700, color: INK_SOFT, minWidth: 24 }}>
                    {j.number}
                  </span><span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: INK }}>
                    {j.title || "Sans titre"}
                  </span><select
                    value={j.status === "done" ? "termine" : j.status === "pending" || j.status === "blocked" ? "a_faire" : j.status === "in_progress" ? "en_cours" : ["a_faire", "en_cours", "termine"].includes(j.status) ? j.status : "a_faire"}
                    onChange={(e) => updateJalonStatus(j.id, e.target.value)}
                    disabled={savingDetail}
                    style={{
                      padding: "6px 12px",
                      borderRadius: 8,
                      border: `1px solid ${BORDER}`,
                      fontSize: 12,
                      fontWeight: 600,
                      color: INK,
                      background: WHITE,
                      cursor: savingDetail ? "wait" : "pointer"
                    }}
                  ><option value="a_faire">À faire</option><option value="en_cours">En cours</option><option value="termine">Terminé</option></select></div>
              );
            })}
          </div>
        )}
      </div>
    )}

    {tabFiche === "taches" && (
      <div><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}><div style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: INK_MUTED, fontWeight: 700 }}>
            Tâches du projet
          </div><button
            onClick={() => { setShowTaskForm(true); setTaskForm({ title: "", priority: "normale", due_date: "" }); }}
            style={{
              padding: "8px 16px",
              background: GOLD,
              border: "none",
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 700,
              color: WHITE,
              cursor: "pointer"
            }}
          >
            Nouvelle tâche
          </button></div>

        {showTaskForm && (
          <div style={{ background: GOLD_BG, border: `1px solid ${GOLD_PALE}`, borderRadius: 12, padding: "18px 20px", marginBottom: 20 }}><div style={{ fontSize: 11, fontWeight: 700, color: INK_SOFT, marginBottom: 12 }}>Nouvelle tâche</div><div style={{ display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: 12, alignItems: "end", flexWrap: "wrap" }}><div><label style={{ fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: INK_MUTED, display: "block", marginBottom: 4 }}>Titre</label><input
                  value={taskForm.title}
                  onChange={(e) => setTaskForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Intitulé de la tâche"
                  style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13, color: INK, outline: "none", boxSizing: "border-box" }}
                /></div><div><label style={{ fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: INK_MUTED, display: "block", marginBottom: 4 }}>Priorité</label><select
                  value={taskForm.priority}
                  onChange={(e) => setTaskForm((f) => ({ ...f, priority: e.target.value }))}
                  style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13, color: INK, background: WHITE, outline: "none" }}
                ><option value="basse">Basse</option><option value="normale">Normale</option><option value="haute">Haute</option></select></div><div><label style={{ fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: INK_MUTED, display: "block", marginBottom: 4 }}>Assigné à</label><input
                  value={taskForm.assigned_to}
                  onChange={(e) => setTaskForm((f) => ({ ...f, assigned_to: e.target.value }))}
                  placeholder="Nom"
                  style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13, color: INK, outline: "none", minWidth: 120 }}
                /></div><div><label style={{ fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: INK_MUTED, display: "block", marginBottom: 4 }}>Date limite</label><input
                  type="date"
                  value={taskForm.due_date}
                  onChange={(e) => setTaskForm((f) => ({ ...f, due_date: e.target.value }))}
                  style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13, color: INK, outline: "none" }}
                /></div></div><div style={{ display: "flex", gap: 8, marginTop: 14 }}><button
                onClick={sauvegarderTache}
                disabled={savingTask || !taskForm.title?.trim()}
                style={{ padding: "8px 18px", background: taskForm.title?.trim() ? GOLD : "#DDD", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 700, color: WHITE, cursor: taskForm.title?.trim() ? "pointer" : "not-allowed" }}
              >
                {savingTask ? "Enregistrement…" : "Enregistrer"}
              </button><button
                onClick={() => { setShowTaskForm(false); setTaskForm({ title: "", priority: "normale", due_date: "", assigned_to: "" }); }}
                style={{ padding: "8px 18px", background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: 12, fontWeight: 600, color: INK_SOFT, cursor: "pointer" }}
              >
                Annuler
              </button></div></div>
        )}

        {loadingTasks ? (
          <div style={{ padding: 24, textAlign: "center", color: INK_MUTED, fontSize: 13 }}>Chargement des tâches…</div>
        ) : projectTasks.length === 0 ? (
          <div style={{ padding: "20px", borderRadius: 10, background: SOFT_BG, border: `1px dashed ${BORDER}`, color: INK_MUTED, fontSize: 13 }}>
            Aucune tâche. Cliquez sur « Nouvelle tâche » pour en ajouter.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {projectTasks.map((t) => {
              const statut = getTaskStatut(t.status || "a_faire");
              return (
                <div
                  key={t.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "12px 14px",
                    background: statut.bg,
                    border: `1px solid ${statut.border}`,
                    borderRadius: 10
                  }}
                ><div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}><span style={{ fontSize: 13, fontWeight: 600, color: INK }}>{t.title}</span>
                    {(() => {
                      const p = t.priority || "normale";
                      const ps = getPriorityStyle(p);
                      return (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: ps.color,
                            background: ps.bg,
                            border: `1px solid ${ps.border}`,
                            borderRadius: 999,
                            padding: "3px 8px",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {p === "haute" ? "Haute" : p === "basse" ? "Basse" : "Normale"}
                        </span>
                      );
                    })()}
                  </div><span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: statut.color,
                      background: WHITE,
                      border: `1px solid ${statut.border}`,
                      borderRadius: 999,
                      padding: "3px 10px",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {statut.label}
                  </span><select
                    value={t.status || "a_faire"}
                    onChange={(e) => changerStatutTache(t.id, e.target.value)}
                    style={{
                      padding: "5px 10px",
                      borderRadius: 6,
                      border: `1px solid ${BORDER}`,
                      fontSize: 11,
                      fontWeight: 600,
                      color: INK,
                      background: WHITE,
                      cursor: "pointer"
                    }}
                  >
                    {TASK_STATUTS.map((s) => (
                      <option key={s.id} value={s.id}>{s.label}</option>
                    ))}
                  </select><span style={{ fontSize: 11, fontWeight: 600, color: t.priority === "haute" ? RED : t.priority === "basse" ? INK_MUTED : GOLD }}>
                    {t.priority === "haute" ? "Haute" : t.priority === "basse" ? "Basse" : "Normale"}
                  </span><input
                    type="text"
                    value={t.assigned_to ?? ""}
                    onChange={(e) => setProjectTasks((prev) => prev.map((x) => (x.id === t.id ? { ...x, assigned_to: e.target.value } : x)))}
                    onBlur={(e) => changerAssignedTache(t.id, e.target.value)}
                    placeholder="Assigné à"
                    style={{
                      width: 100,
                      padding: "4px 8px",
                      borderRadius: 6,
                      border: `1px solid ${BORDER}`,
                      fontSize: 11,
                      color: INK,
                      background: WHITE,
                      outline: "none",
                      boxSizing: "border-box"
                    }}
                  /><span style={{ fontSize: 12, color: INK_MUTED }}>{t.due_date ? fmtDate(t.due_date) : "—"}</span></div>
              );
            })}
          </div>
        )}
      </div>
    )}

    {tabFiche === "documents" && (
      <div
        style={{
          padding: "20px",
          borderRadius: 10,
          background: SOFT_BG,
          border: `1px dashed ${BORDER}`,
          color: INK_MUTED,
          fontSize: 13
        }}
      >
        Module documents à connecter à la fiche projet.
      </div>
    )}

    {tabFiche === "generation" && (
      <div style={{ padding: "4px 0" }}><div style={{ fontSize: 12, color: INK_MUTED, marginBottom: 20 }}>
          Génération des livrables à partir du cahier des charges. Chaque module peut être lancé indépendamment.
        </div>

        {/* Bloc Moodboard */}
        <div
          style={{
            marginBottom: 24,
            padding: "20px 22px",
            borderRadius: 14,
            background: WHITE,
            border: `1px solid ${BORDER}`,
          }}
        ><div style={{ fontSize: 13, fontWeight: 700, color: INK, marginBottom: 16 }}>Moodboard</div><div style={{ fontSize: 12, color: INK_SOFT, marginBottom: 16 }}>
            Données projet / cahier des charges utilisées pour la génération (style, couleurs, matériaux, type, budget, pièces).
          </div>
          {selectedProject && (() => {
            const brief = extraireBriefProjet(selectedProject, isOPCMandate);
            return (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                  gap: 12,
                  marginBottom: 20,
                }}
              >
                <div>
                  <span style={{ fontSize: 10, color: INK_MUTED, textTransform: "uppercase" }}>Type de projet</span>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{brief.typeProjet}</div>
                </div>

                {!isOPCMandate && (
                  <div>
                    <span style={{ fontSize: 10, color: INK_MUTED, textTransform: "uppercase" }}>Budget</span>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{brief.budget}</div>
                  </div>
                )}

                <div>
                  <span style={{ fontSize: 10, color: INK_MUTED, textTransform: "uppercase" }}>Style</span>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{brief.style}</div>
                </div>
                <div>
                  <span style={{ fontSize: 10, color: INK_MUTED, textTransform: "uppercase" }}>Couleurs</span>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{brief.couleurs}</div>
                </div>
                <div>
                  <span style={{ fontSize: 10, color: INK_MUTED, textTransform: "uppercase" }}>Matériaux</span>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{brief.materiaux}</div>
                </div>
                <div>
                  <span style={{ fontSize: 10, color: INK_MUTED, textTransform: "uppercase" }}>Pièces concernées</span>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{brief.pieces}</div>
                </div>
              </div>
            );
          })()}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: moodboardGenerated ? 20 : 0 }}><button
              type="button"
              onClick={() => {
                if (!selectedProject) return;
                const brief = extraireBriefProjet(selectedProject, isOPCMandate);
                setMoodboardGenerated(genererStructureMoodboard(selectedProject, brief));
              }}
              style={{
                padding: "10px 18px",
                borderRadius: 8,
                border: "none",
                background: GOLD,
                color: WHITE,
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Générer le moodboard
            </button>
            {moodboardGenerated && (
              <button
                type="button"
                onClick={() => exporterBriefMoodboard(moodboardGenerated)}
                style={{
                  padding: "10px 18px",
                  borderRadius: 8,
                  border: `1px solid ${GOLD}`,
                  background: GOLD_BG,
                  color: GOLD,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Exporter le brief moodboard
              </button>
            )}
          </div>
          {moodboardGenerated && (
            <div style={{ paddingTop: 20, borderTop: `1px solid ${BORDER}` }}><div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: GOLD, fontWeight: 700, marginBottom: 12 }}>Résultat — structure moodboard</div><div style={{ display: "flex", flexDirection: "column", gap: 14 }}><div><span style={{ fontSize: 10, color: INK_MUTED }}>Style dominant</span><div style={{ fontSize: 13, fontWeight: 600 }}>{moodboardGenerated.styleDominant}</div></div><div><span style={{ fontSize: 10, color: INK_MUTED }}>Palette couleurs</span><div style={{ fontSize: 13 }}>{moodboardGenerated.paletteCouleurs}</div></div><div><span style={{ fontSize: 10, color: INK_MUTED }}>Matériaux recommandés</span><div style={{ fontSize: 13 }}>{moodboardGenerated.materiauxRecommandes}</div></div><div><span style={{ fontSize: 10, color: INK_MUTED }}>Ambiance</span><div style={{ fontSize: 12, color: INK_SOFT, lineHeight: 1.6 }}>{moodboardGenerated.ambiance}</div></div><div><span style={{ fontSize: 10, color: INK_MUTED, display: "block", marginBottom: 6 }}>Suggestions visuelles par pièce</span><div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {moodboardGenerated.suggestionsParPiece.map((s, i) => (
                      <div key={i} style={{ padding: "10px 12px", background: SOFT_BG, borderRadius: 8, border: `1px solid ${BORDER}` }}><div style={{ fontSize: 12, fontWeight: 700, color: INK, marginBottom: 4 }}>{s.piece}</div><div style={{ fontSize: 12, color: INK_SOFT }}>{s.suggestion}</div></div>
                    ))}
                  </div></div></div><div style={{ fontSize: 11, color: INK_MUTED, marginTop: 12 }}>Généré le {moodboardGenerated.dateGeneration}</div></div>
          )}
        </div>

        {/* Autres modules */}
        <div style={{ fontSize: 12, fontWeight: 700, color: INK_MUTED, marginBottom: 12 }}>Autres modules</div><div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 14 }}>
          {[
            { id: "plans_2d", label: "Plans 2D", description: "Plan d'aménagement 2D à partir du cahier des charges." },
            { id: "visuels_3d", label: "Visuels 3D", description: "Visuels 3D basés sur le plan d'aménagement." },
            { id: "shopping_list", label: "Shopping list", description: "Liste FF&E (mobilier, équipements, accessoires)." },
            { id: "documents_techniques", label: "Documents techniques", description: "APD technique, dossier technique, CCTP, dossier administratif." },
          ].map((mod) => (
            <div
              key={mod.id}
              style={{
                padding: "16px 18px",
                borderRadius: 12,
                background: WHITE,
                border: `1px solid ${BORDER}`,
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            ><div style={{ fontSize: 13, fontWeight: 700, color: INK }}>{mod.label}</div><div style={{ fontSize: 11, color: INK_SOFT, lineHeight: 1.5, flex: 1 }}>{mod.description}</div><button
                type="button"
                onClick={() => alert(`Génération « ${mod.label} » — à connecter (projet: ${selectedProject?.name || selectedProject?.id}).`)}
                style={{
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: `1px solid ${BORDER}`,
                  background: SOFT_BG,
                  color: INK_SOFT,
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: "pointer",
                  alignSelf: "flex-start",
                }}
              >
                Lancer la génération
              </button></div>
          ))}
        </div></div>
    )}

    {tabFiche === "notes" && (
      <div><textarea
          value={selectedProject.description || ""}
          onChange={(e) => setSelectedProject(prev => ({ ...prev, description: e.target.value }))}
          rows={6}
          placeholder="Notes internes du projet…"
          style={{
            width: "100%",
            padding: "12px 14px",
            borderRadius: 8,
            border: `1px solid ${BORDER}`,
            fontSize: 13,
            color: INK,
            resize: "vertical",
            outline: "none",
            boxSizing: "border-box"
          }}
        /><div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}><button
            onClick={enregistrerNotesProjet}
            disabled={savingDetail}
            style={{
              padding: "10px 18px",
              background: GOLD,
              border: "none",
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 700,
              color: WHITE,
              cursor: savingDetail ? "wait" : "pointer",
              opacity: savingDetail ? 0.7 : 1
            }}
          >
            {savingDetail ? "Enregistrement…" : "Enregistrer les notes"}
          </button></div></div>
    )}
  </div>
)}
      {/* LISTE */}
      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: INK_MUTED, fontStyle: "italic" }}>
          Chargement…
        </div>
      ) : projects.length === 0 ? (
        <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 14,
          padding: "48px 24px", textAlign: "center" }}><div style={{ fontSize: 26, fontFamily: "'Playfair Display', serif", color: "#3D2E20", marginBottom: 8 }}>Aucun projet pour le moment</div><div style={{ fontSize: 12, color: INK_MUTED, marginBottom: 20 }}>
            {opcNoAssignments
              ? "Aucun projet assigné. Contactez votre administrateur."
              : search || filterStatut !== "tous"
                ? "Aucun résultat pour ce filtre"
                : "Commencez par créer votre premier projet"}
          </div>
          {canCreateProject && !search && filterStatut === "tous" && !opcNoAssignments && (
            <button onClick={() => { setForm(FORM_VIDE); setEditId(null); setShowForm(true); }}
              style={{ padding: "10px 24px", background: GOLD, border: "none", borderRadius: 8,
                fontSize: 12, fontWeight: 700, color: WHITE, cursor: "pointer" }}>
              ＋ Nouveau projet
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 12 }}>
          {projects.map(p => {
            const statut = getStatut(p.status);
            return (
              <div key={p.id} style={{ background: WHITE, border: `1px solid ${BORDER}`,
                borderRadius: 12, padding: "18px 20px",
                transition: "transform .2s ease, box-shadow .2s ease" }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 8px 22px rgba(61,46,32,0.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              ><div style={{ display: "flex", alignItems: "center", gap: 14 }}>

                  {/* Infos principales */}
                  <div style={{ flex: 1 }}><div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}><div
  onClick={() => ouvrirProjet(p)}
  style={{
    fontFamily: "'Playfair Display', serif",
    fontSize: 18,
    fontWeight: 700,
    color: "#3D2E20",
    cursor: "pointer",
    lineHeight: 1.2
  }}
>
  {p.name}
</div><span style={{ fontSize: 10, fontWeight: 700, color: statut.color,
                        background: statut.bg, border: `1px solid ${statut.border}`,
                        borderRadius: 999, padding: "2px 10px" }}>
                        {statut.label}
                      </span></div>{!isOPCMandate && (
                    <div style={{ fontSize: 12, color: INK_SOFT, marginBottom: 10 }}>
                      {p.client_name || "Client non renseigné"}
                    </div>
                    )}
                    {!isOPCMandate && (
                      <div
                        style={{
                          fontFamily: "'Playfair Display', serif",
                          fontSize: 24,
                          color: "#C09A5B",
                          fontWeight: 700,
                          marginBottom: 8,
                        }}
                      >
                        {madDisplay(p.budget)}
                      </div>
                    )}
                    {!isOPCMandate && p.description && <div style={{ fontSize: 11, color: INK_MUTED, marginBottom: 8, lineHeight: 1.5 }}>{p.description}</div>}
                    {/* Barre de progression */}
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}><div style={{ flex: 1, height: 6, background: SOFT_BG, borderRadius: 999, overflow: "hidden", maxWidth: 300 }}><div style={{ height: "100%", width: `${p.progress || 0}%`,
                          background: "#C09A5B", borderRadius: 999, transition: "width 0.4s" }} /></div><span style={{ fontSize: 11, fontWeight: 700, color: "#C09A5B", minWidth: 30 }}>
                        {p.progress || 0}%
                      </span></div></div>

                  {/* Actions */}
                  <div style={{ display: "flex", gap: 8, flexShrink: 0 }}><button
    onClick={() => ouvrirProjet(p)}
    style={{
      padding: "8px 16px",
      background: WHITE,
      border: `1px solid ${GOLD_PALE}`,
      borderRadius: 8,
      fontSize: 12,
      fontWeight: 600,
      color: GOLD,
      cursor: "pointer"
    }}
  >
    Ouvrir
  </button>{!clientRestricted && (
  <>
  <button
    onClick={() => editer(p)}
    style={{
      padding: "8px 16px",
      background: SOFT_BG,
      border: `1px solid ${BORDER}`,
      borderRadius: 8,
      fontSize: 12,
      fontWeight: 600,
      color: INK,
      cursor: "pointer"
    }}
  >
    Modifier
  </button><button
    onClick={() => setConfirmDel(p.id)}
    style={{
      padding: "8px 14px",
      background: RED_BG,
      border: `1px solid ${RED_BORDER}`,
      borderRadius: 8,
      fontSize: 12,
      color: RED,
      cursor: "pointer"
    }}
  >
    Supprimer
  </button>
  </>
  )}
</div></div></div>
            );
          })}
        </div>
      )}

      {/* CONFIRM SUPPRESSION */}
      {confirmDel && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}><div style={{ background: WHITE, borderRadius: 14, padding: "32px 36px",
            maxWidth: 400, width: "90%", textAlign: "center" }}><div style={{ fontSize: 28, marginBottom: 12 }}>—</div><div style={{ fontSize: 15, fontWeight: 700, color: INK, marginBottom: 8 }}>
              Supprimer ce projet ?
            </div><div style={{ fontSize: 12, color: INK_MUTED, marginBottom: 24 }}>
              Cette action est irréversible.
            </div><div style={{ display: "flex", gap: 10, justifyContent: "center" }}><button onClick={() => setConfirmDel(null)}
                style={{ padding: "10px 24px", background: WHITE, border: `1px solid ${BORDER}`,
                  borderRadius: 8, fontSize: 12, fontWeight: 600, color: INK, cursor: "pointer" }}>
                Annuler
              </button><button onClick={() => supprimer(confirmDel)}
                style={{ padding: "10px 24px", background: RED, border: "none",
                  borderRadius: 8, fontSize: 12, fontWeight: 700, color: WHITE, cursor: "pointer" }}>
                Supprimer
              </button></div></div></div>
      )}

    </div>
  );
}
